import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'

import { cn } from '@/lib/utils'

const Popover = PopoverPrimitive.Root
const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'start', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 rounded-md border bg-popover p-3 text-popover-foreground shadow-md outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        'data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent }

const WEEKDAYS = ['一', '二', '三', '四', '五', '六', '日']

/**
 * Default clock time for a date picked without one.
 *
 * Registry expiry timestamps sit at the end of the day, and the field this
 * serves is almost always an expiry. Defaulting to 23:59 means the common case
 * needs no interaction with the time field at all; the alternative, an empty
 * time, is what made the native control tedious.
 */
const DEFAULT_TIME = '23:59'

/** Splits the `YYYY-MM-DDTHH:mm` form value the native input also used. */
function splitValue(value: string): { date: string; time: string } {
  const [date = '', time = ''] = value.split('T')
  return { date, time }
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/** Local calendar date as `YYYY-MM-DD`, avoiding a UTC round trip. */
function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function parseDateKey(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

export interface DateTimePickerProps {
  /** `YYYY-MM-DDTHH:mm` in the browser's own timezone, or '' for unset. */
  value: string
  onChange: (value: string) => void
  /** Emitted as a hidden input so surrounding FormData submits keep working. */
  name?: string
  id?: string
  placeholder?: string
  'aria-label'?: string
  disabled?: boolean
  className?: string
}

/**
 * Calendar-and-clock replacement for `<input type="datetime-local">`.
 *
 * The native control makes you tab through year/month/day/hour/minute segments
 * in an order that differs per browser and locale, with no way to see the month
 * you are aiming at. For picking a date years out — which is what a domain
 * expiry is — that is the wrong shape entirely.
 *
 * The value format is unchanged from the native input so callers, their
 * conversion helpers, and any FormData submit keep working as they did.
 */
export function DateTimePicker({
  value,
  onChange,
  name,
  id,
  placeholder = '选择日期',
  disabled,
  className,
  ...aria
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const { date, time } = splitValue(value)
  const selected = parseDateKey(date)

  // The visible month follows the selection, but only while the popover is
  // closed: re-deriving it during browsing would yank the view back on every
  // pick and make paging through months impossible.
  const [viewMonth, setViewMonth] = React.useState(() => selected ?? new Date())
  React.useEffect(() => {
    if (!open) setViewMonth(selected ?? new Date())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date])

  const year = viewMonth.getFullYear()
  const month = viewMonth.getMonth()
  const todayKey = toDateKey(new Date())

  const cells = React.useMemo(() => {
    const first = new Date(year, month, 1)
    // Monday-first, matching the weekday header.
    const lead = (first.getDay() + 6) % 7
    const days = new Date(year, month + 1, 0).getDate()
    const out: (Date | null)[] = Array.from({ length: lead }, () => null)
    for (let d = 1; d <= days; d++) out.push(new Date(year, month, d))
    return out
  }, [year, month])

  function commit(nextDate: string, nextTime: string) {
    if (!nextDate) {
      onChange('')
      return
    }
    onChange(`${nextDate}T${nextTime || DEFAULT_TIME}`)
  }

  function pickDay(d: Date) {
    commit(toDateKey(d), time)
    setOpen(false)
  }

  const label = selected
    ? `${date} ${time || DEFAULT_TIME}`
    : placeholder

  return (
    <div className={cn('relative', className)}>
      {name && <input type="hidden" name={name} value={value} />}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-label={aria['aria-label']}
            className={cn(
              'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm',
              'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50',
              !selected && 'text-muted-foreground',
            )}
          >
            <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="flex-1 text-left tabular-nums">{label}</span>
            {selected && !disabled && (
              <span
                role="button"
                tabIndex={0}
                aria-label="清除日期"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange('')
                  }
                }}
                className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </span>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-[17.5rem]">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewMonth(new Date(year, month - 1, 1))}
              aria-label="上一月"
              className="rounded p-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <div className="flex items-center gap-1 text-sm font-medium tabular-nums">
              <select
                value={year}
                onChange={(e) => setViewMonth(new Date(Number(e.target.value), month, 1))}
                aria-label="年份"
                className="rounded bg-transparent px-1 py-0.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {/* Wide enough for a ten-year registration term in either direction. */}
                {Array.from({ length: 31 }, (_, i) => new Date().getFullYear() - 15 + i).map((y) => (
                  <option key={y} value={y}>{y} 年</option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => setViewMonth(new Date(year, Number(e.target.value), 1))}
                aria-label="月份"
                className="rounded bg-transparent px-1 py-0.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {Array.from({ length: 12 }, (_, i) => i).map((m) => (
                  <option key={m} value={m}>{m + 1} 月</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setViewMonth(new Date(year, month + 1, 1))}
              aria-label="下一月"
              className="rounded p-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center text-[0.7rem] text-muted-foreground">
            {WEEKDAYS.map((d) => <div key={d} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (!d) return <div key={`pad-${i}`} />
              const key = toDateKey(d)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickDay(d)}
                  aria-current={key === todayKey ? 'date' : undefined}
                  className={cn(
                    'h-8 rounded text-sm tabular-nums transition-colors hover:bg-accent',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    key === todayKey && 'font-semibold text-primary',
                    key === date && 'bg-primary text-primary-foreground hover:bg-primary',
                  )}
                >
                  {d.getDate()}
                </button>
              )
            })}
          </div>

          <div className="mt-3 flex items-center gap-2 border-t pt-3">
            <label className="text-xs text-muted-foreground" htmlFor={`${id ?? 'dtp'}-time`}>
              时间
            </label>
            <input
              id={`${id ?? 'dtp'}-time`}
              type="time"
              value={time || DEFAULT_TIME}
              onChange={(e) => commit(date || todayKey, e.target.value)}
              className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              type="button"
              onClick={() => {
                commit(todayKey, time)
                setOpen(false)
              }}
              className="rounded-md border px-2 py-1 text-xs hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              今天
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
